import { defaultContainer } from './container-instance.class.mjs';
/**
 * A collection of individual visitor objects, combined into a collection.
 * This class implements the {@link ContainerTreeVisitor} interface
 * (modulo {@link ContainerTreeVisitor.visitContainer}).
 *
 * When an event is dispatched upon the collection, all attached
 * visitors will be notified in the order in which they were
 * added to the collection.
 * @experimental
 *
 * @example
 * ```ts
 * const collection = new VisitorCollection();
 *
 * // Add our visitor to the collection.
 * collection.addVisitor(new MyVisitor());
 *
 * // Dispatch an event onto the collection,
 * // which will be broadcast to all attached visitors.
 * collection.visitChildContainer(Container.ofChild('hello'));
 * ```
 *
 * @group Tree Visitors
 */
export class VisitorCollection {
    constructor() {
        /** Whether the instance is disposed. */
        this.disposed = false;
        /** The visitors stored within the collection. */
        this.visitors = [];
        /**
         * A flag stating whether any visitors are currently present in the collection.
         * If not, all notifications will be ignored.
         *
         * This is mostly for optimisation.
         */
        this.anyVisitorsPresent = false;
    }
    /**
     * A flag which states whether any visitors should be notified.
     * If the collection has been disposed or no visitors are present,
     * this evaluates to `false`.
     */
    get notifyVisitors() {
        return !this.disposed && this.anyVisitorsPresent;
    }
    /**
     * Iterate the list of visitors, excluding any which have been disposed.
     * Does not perform any iteration if no visitors are present.
     *
     * @param callback A function to call for each visitor in the collection.
     */
    forEachVisitor(callback) {
        if (this.notifyVisitors) {
            this.visitors.forEach(visitor => {
                /** If the visitor has been disposed since the last iteration, free it from the store. */
                if (visitor.disposed) {
                    this.removeVisitorFromCollection(visitor);
                    return;
                }
                /** Disposed visitors should not be notified. */
                callback(visitor);
            });
        }
    }
    /**
     * Notify all attached visitors of a newly-created child container.
     * @internal
     *
     * @see {@link ContainerTreeVisitor.visitChildContainer}
     */
    notifyChildContainerVisited(child) {
        this.forEachVisitor(visitor => visitor.visitChildContainer?.(child));
    }
    /**
     * Notify all attached visitors of a newly-created orphaned container.
     * @internal
     *
     * @see {@link ContainerTreeVisitor.visitOrphanedContainer}
     */
    notifyOrphanedContainerVisited(container) {
        this.forEachVisitor(visitor => visitor.visitOrphanedContainer?.(container));
    }
    /**
     * Notify all attached visitors of a newly-created service.
     * @internal
     *
     * @see {@link ContainerTreeVisitor.visitNewService}
     */
    notifyNewServiceVisited(serviceOptions) {
        this.forEachVisitor(visitor => visitor.visitNewService?.(serviceOptions));
    }
    /**
     * Notify all attached visitors of a retrieval.
     * @internal
     *
     * @see {@link ContainerTreeVisitor.visitRetrieval}
     */
    notifyRetrievalVisited(identifier, options) {
        this.forEachVisitor(visitor => visitor.visitRetrieval?.(identifier, options));
    }
    /**
     * Add a visitor to the collection.
     * @experimental
     *
     * @param visitor The visitor to append to the collection.
     * @param container The container to initialise the container on.
     */
    addVisitorToCollection(visitor, container) {
        /** If the visitor is already present, do not add another. */
        if (this.visitors.includes(visitor)) {
            return false;
        }
        if ('visitOrphanedContainer' in visitor && container !== defaultContainer) {
            /**
             * Orphaned container events are only dispatched on the default container.
             * Therefore, we need to create a proxy that forwards this event from the
             * default container to this visitor.
             */
            VisitorCollection.forwardOrphanedContainerEvents(visitor);
        }
        this.visitors.push(visitor);
        /**
         * Mark the collection as having visitors present, so
         * it doesn't ignore any events passed to it.
         */
        if (!this.anyVisitorsPresent) {
            this.anyVisitorsPresent = true;
        }
        let isAllowedToAttachVisitor;
        try {
            /**
             * Directly call the visitor's "visitContainer" method
             * to initialise it upon the given container.
             *
             * Implementation detail: We don't provide any sort of protection against
             * this being called again on a different container.
             * The visitor would be able to handle that themselves, as the API states
             * that any call to `visitContainer` means that the visitor was added
             * to a container.
             */
            isAllowedToAttachVisitor = visitor.visitContainer?.(container);
        }
        finally {
            /**
             * If a false-y return value was returned to signal that the visitor
             * should not be attached to the given container, immediately remove it.
             */
            if (!isAllowedToAttachVisitor) {
                this.removeVisitorFromCollection(visitor);
            }
        }
        return isAllowedToAttachVisitor;
    }
    /**
     * Remove a visitor from the collection,
     * if it was already present.
     * @experimental
     *
     * @param visitor The visitor to remove from the collection.
     */
    removeVisitorFromCollection(visitor) {
        const indexOfVisitor = this.visitors.indexOf(visitor);
        if (indexOfVisitor === -1) {
            return false;
        }
        /** Remove the element from the array via "splice". */
        this.visitors.splice(indexOfVisitor, 1);
        /** Re-evalute the length of the visitors array and recompute anyVisitorsPresent. */
        if (this.visitors.length === 0) {
            this.anyVisitorsPresent = false;
        }
        return true;
    }
    /**
     * Forward any orphaned container events to the visitor
     * through a proxy visitor attached to the default container.
     *
     * This is used when a visitor implements the {@link ContainerTreeVisitor.visitOrphanedContainer}
     * handler, but the visitor was attached to a non-default container instance.
     */
    static forwardOrphanedContainerEvents(upstreamVisitor) {
        const proxyVisitor = {
            get disposed() {
                return upstreamVisitor.disposed;
            },
            dispose() {
                /**
                 * We don't forward the disposal of this proxy visitor upstream, as doing so may cause
                 * unintended consequences.  For all purposes, this visitor is invisible to the upstream
                 * one, and so any state it encounters should be kept at bay.
                 */
                this.disposed = true;
            },
            visitOrphanedContainer(container) {
                if (!this.disposed) {
                    /** Proxy the call to the upstream visitor. */
                    upstreamVisitor.visitOrphanedContainer(container);
                }
            },
            visitContainer(container) {
                /**
                 * The check here is just for redundancy.  It ensures that, in the event of attachment
                 * to a non-default container, this visitor would immediately become unmounted.
                 *
                 * This is done to be 100% sure that the visitor is launched in the correct container.
                 * One instance where this could be useful is if the visitors of the default container
                 * are blindly copied to another container.
                 */
                return container === defaultContainer;
            },
        };
        return defaultContainer.acceptTreeVisitor(proxyVisitor);
    }
    async dispose() {
        /** Prevent duplicate calls to dispose. */
        if (this.disposed) {
            return;
        }
        this.disposed = true;
        /** Return a mega-promise of all the visitors' disposal results. */
        await Promise.all(this.visitors.map(visitor => visitor.dispose()));
    }
}
//# sourceMappingURL=visitor-collection.class.mjs.map